import { batchUrls, type OmittedUrl } from './api.js';
import { me } from './state.js';

/**
 * Les URL de lecture — **groupées**, depuis 4.4.
 *
 * Jusqu'ici c'était une présignature par image, donc un appel par image, protégé
 * par une fenêtre de six appels concurrents (D-22). À vingt photos c'était
 * invisible ; à deux cents, c'étaient deux cents allers-retours contre un stage
 * étranglé à 20 req/s — la moitié partaient en `429` avant qu'une seule vignette
 * n'apparaisse. `POST /files/urls` remplace tout cela par **un appel par vague**.
 *
 * Trois mécanismes, et chacun répond à un défaut précis :
 *
 * ① **Le regroupement par trame.** Une grille virtualisée monte cinquante tuiles
 *    dans le même tour de boucle ; les demander séparément referait exactement ce
 *    qu'on vient de retirer. Elles sont accumulées puis envoyées ensemble.
 * ② **La mémoire, pour des octets immuables seulement.** Les dérivés d'un média le
 *    sont — un nouvel envoi crée un nouveau `fid`, jamais une seconde version du
 *    même. Le corps d'une note ne l'est pas : son URL est pinnée sur
 *    `ContentVersionId`, et la mémoriser fait relire la révision d'avant après
 *    chaque enregistrement. Un parcours de 4.3 l'a trouvé, et cela se lisait
 *    exactement comme une perte de données.
 * ③ **La mémoire des refus.** Sans elle, un `fid` illisible serait redemandé à
 *    chaque re-rendu, indéfiniment : le lot qui devait sauver la page se
 *    remplirait de ce que le serveur refuse déjà. Seul `bytes_not_ready` est
 *    oubliable — c'est le seul des trois qui veut dire « pas encore ».
 *
 * L'entrée mémorisée **expire avant l'URL**. Une présignature vaut quelques
 * minutes ; la garder plus longtemps ferait afficher une image morte à qui laisse
 * son onglet ouvert, ce qui est le symptôme le plus difficile à rapporter.
 */

/** Les rôles dont les octets peuvent changer sous un `fid` constant. */
const MUTABLE = new Set(['content']);

/** Repli si `GET /me` n'a pas encore répondu. Le serveur reste seul juge. */
const BATCH_FALLBACK = 200;

/**
 * Une trame. Assez pour qu'une vague de montage se referme, assez peu pour que la
 * première vignette n'attende pas : au-delà, l'attente devient perceptible sur une
 * grille qui ne monte qu'une tuile.
 */
const COALESCE_MS = 16;

interface Held {
  readonly url: string;
  readonly until: number;
}

const held = new Map<string, Held>();
const refused = new Map<string, OmittedUrl['reason']>();

interface Waiting {
  readonly fid: string;
  readonly role: string;
  readonly gid?: string;
  readonly resolve: (url: string) => void;
  readonly reject: (err: unknown) => void;
}

let queue = new Map<string, Waiting[]>();
let timer: ReturnType<typeof setTimeout> | undefined;

const key = (fid: string, role: string): string => `${fid}#${role}`;

/** L'URL déjà connue, si elle l'est et si elle est encore bonne. Sans appel. */
export function knownUrl(fid: string, role = 'thumb'): string | undefined {
  if (MUTABLE.has(role)) return undefined;
  const entry = held.get(key(fid, role));
  return entry && entry.until > Date.now() ? entry.url : undefined;
}

/**
 * Un refus **définitif** déjà connu. L'écran s'en sert pour ne pas laisser un
 * emplacement vide sans explication : une vignette absente parce qu'elle est
 * illisible n'est pas une vignette en cours de chargement.
 */
export function refusedUrl(fid: string, role = 'thumb'): OmittedUrl['reason'] | undefined {
  return refused.get(key(fid, role));
}

export function urlFor(fid: string, role = 'thumb', gid?: string): Promise<string> {
  const known = knownUrl(fid, role);
  if (known) return Promise.resolve(known);

  const k = key(fid, role);
  const refusal = refused.get(k);
  if (refusal) return Promise.reject(new Error(refusal));

  return new Promise<string>((resolve, reject) => {
    // **Le vol de demandes concurrentes.** Deux tuiles pour le même `fid` — une
    // dans la grille, une dans l'inspecteur — ne produisent qu'une entrée de lot.
    const existing = queue.get(k);
    if (existing) {
      existing.push({ fid, role, ...(gid ? { gid } : {}), resolve, reject });
    } else {
      queue.set(k, [{ fid, role, ...(gid ? { gid } : {}), resolve, reject }]);
    }
    if (timer === undefined) timer = setTimeout(() => void flush(), COALESCE_MS);
  });
}

/**
 * **L'image embarquée dans une note** — le rôle `standard`, et l'attente que
 * l'envoi impose *(N2, 2026-08-17)*.
 *
 * `urlFor` seul ne suffit pas ici, et c'est le seul endroit du site où c'est
 * vrai. Partout ailleurs on demande les octets d'un média qui existe depuis
 * longtemps ; dans une note, on vient parfois de l'envoyer — l'entité est
 * `PROCESSING`, le dérivé `standard` n'est pas né, et le serveur omet la clé en
 * **`bytes_not_ready`**. Sans attente, une photo qu'on vient d'insérer
 * s'afficherait comme son texte de remplacement, c'est-à-dire exactement comme
 * une photo supprimée.
 *
 * `bytes_not_ready` est **le seul refus qu'on réessaie**, et c'est déjà la règle
 * du cache ci-dessus : c'est le seul des trois qui veut dire « pas encore ». Un
 * `unreadable` ou un `not_found` ne changera pas d'avis.
 */
export async function noteImageUrl(fid: string, tries = 12, everyMs = 2_500): Promise<string> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await urlFor(fid, 'standard');
    } catch (err) {
      const reason = err instanceof Error ? err.message : '';
      if (reason !== 'bytes_not_ready' || attempt >= tries) throw err;
      await new Promise((resolve) => setTimeout(resolve, everyMs));
    }
  }
}

async function flush(): Promise<void> {
  timer = undefined;
  const wave = queue;
  queue = new Map();
  if (wave.size === 0) return;

  const max = me.value?.limits?.['maxUrlsPerBatch'] ?? BATCH_FALLBACK;
  const entries = [...wave.entries()];

  for (let i = 0; i < entries.length; i += max) {
    const chunk = entries.slice(i, i + max);
    await send(chunk);
  }
}

async function send(chunk: readonly [string, Waiting[]][]): Promise<void> {
  const byKey = new Map(chunk);
  const request = chunk.map(([, waiters]) => {
    const first = waiters[0]!;
    return { fid: first.fid, role: first.role, ...(first.gid ? { gid: first.gid } : {}) };
  });

  let res: Awaited<ReturnType<typeof batchUrls>>;
  try {
    res = await batchUrls(request);
  } catch (err) {
    // Une panne de lot est une panne pour tout le monde : personne ne reste
    // suspendu. C'est la contrepartie du regroupement, et elle est correcte —
    // l'appelant réessaiera au prochain rendu, la file étant vide.
    for (const [, waiters] of chunk) for (const w of waiters) w.reject(err);
    return;
  }

  const now = Date.now();
  const served = new Set<string>();

  for (const item of res.items) {
    const k = key(item.fid, item.role);
    served.add(k);
    // 80 % de la durée annoncée : la marge couvre le chargement de l'image
    // elle-même, qui commence après.
    if (!MUTABLE.has(item.role)) {
      held.set(k, { url: item.url, until: now + item.expiresIn * 800 });
    }
    for (const w of byKey.get(k) ?? []) w.resolve(item.url);
  }

  for (const omission of res.omitted) {
    const k = key(omission.fid, omission.role);
    served.add(k);
    // **`bytes_not_ready` n'est pas mémorisé** : c'est le seul refus qui veut dire
    // « pas encore ». Le worker finira, et la tuile redemandera.
    if (omission.reason !== 'bytes_not_ready') refused.set(k, omission.reason);
    for (const w of byKey.get(k) ?? []) w.reject(new Error(omission.reason));
  }

  // Ni servi, ni omis : le serveur n'a pas répondu sur cette clé. Ne rien faire la
  // laisserait suspendue pour toujours, et la tuile tournerait indéfiniment.
  for (const [k, waiters] of chunk) {
    if (served.has(k)) continue;
    for (const w of waiters) w.reject(new Error('unanswered'));
  }
}

/** À la déconnexion : ces URL portent des signatures sur des octets privés. */
export function forgetUrls(): void {
  held.clear();
  refused.clear();
}
